import { MAKER_DIRECTORY_ENABLED } from '@/lib/flags'
import type { MakerProfileInput } from '@/api/makerProfile'

// Controlled vocabulary + caps for the Find a Maker directory (F6).
//
// MATERIALS is the curated quick-pick set the editor offers as one-tap chips
// (SR-16). It is NOT a closed allowlist: makers may also add custom materials
// (comma-separated free text). The backend
// (backend/api-go/internal/api/makers/validate.go) accepts this curated set OR
// any sanitized custom label (trimmed, 1-40 chars). Keep these labels in sync
// with the backend `curatedMaterials` set.
export const MATERIALS = ['PLA', 'ASA', 'Resin', 'PETG', 'ABS'] as const

// PRINTERS is a curated, non-exhaustive list of common FDM/resin printers the
// editor offers as multi-select options. The backend accepts free text, so the
// editor also allows a custom "Other" value.
export const PRINTERS = [
  'Bambu Lab X1C',
  'Bambu Lab X1E',
  'Bambu Lab P1S',
  'Bambu Lab P1P',
  'Bambu Lab A1',
  'Bambu Lab A1 mini',
  'Prusa MK4',
  'Prusa MK3S+',
  'Prusa XL',
  'Creality K1',
  'Creality K1 Max',
  'Creality Ender 3',
  'Voron 2.4',
  'Elegoo Saturn 3',
  'Snapmaker U1',
] as const

/** Backend cap on a profile's color inventory (validate.go: > 32 rejects). */
export const MAX_COLOR_SWATCHES = 32

/** Backend cap on sample photos per profile (constants.MaxMakerPhotos). */
export const MAX_PHOTOS = 8

// The photo-upload constants + downscale helper are shared with review photos;
// they live in lib/images and are re-exported here for existing maker callers.
export { MAX_PHOTO_BYTES, MAX_PHOTO_DIM, PHOTO_JPEG_QUALITY, downscaleImage } from './images'

export const HEX_COLOR_RE = /^#[0-9a-fA-F]{6}$/

export function isValidHexColor(v: string): boolean {
  return HEX_COLOR_RE.test(v)
}

/** Shared build-time gate for every Find a Maker surface. */
export function isMakerDirectoryEnabled(): boolean {
  return MAKER_DIRECTORY_ENABLED
}

/**
 * The Code of Conduct version the editor submits. MUST match the backend's
 * `CurrentCoCVersion` (F4) byte for byte; a mismatch makes every submit fail
 * acceptance. Bumping the backend constant forces every maker to re-accept.
 */
export const CURRENT_COC_VERSION = '2026-09-01'

// ─── Field validation (mirrors backend validate.go) ────────────────────────────
//
// The editor validates the OUTGOING payload (MakerProfileInput) against the same
// rules the Go handler enforces (backend/api-go/internal/api/makers/validate.go)
// so a submit is blocked client-side with inline field errors before the
// round-trip — and the SAME dotted-key vocabulary is used for the server's 422
// `fields`, so `mapFieldErrors` renders both identically.

/** Backend caps mirrored from validate.go / MakerProfileForm limits. */
export const MAKER_LIMITS = {
  displayName: 80,
  bio: 600,
  region: 80,
  city: 80,
  shipsTo: 40,
  printers: 20,
  printerName: 60,
  materials: 12,
  materialLabel: 40,
  capabilityTags: 20,
  capabilityTag: 40,
  turnaround: 120,
  pricingNote: 200,
  maxColorsMin: 1,
  maxColorsMax: 16,
  colors: MAX_COLOR_SWATCHES,
  colorName: 40,
  contactUrl: 300,
  email: 254,
} as const

const COUNTRY_CODE_RE = /^[A-Z]{2}$/
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
// Mirrors validate.go materialLabelRe: a sanitized, printable 1-40 char label.
const MATERIAL_LABEL_RE = /^[\p{L}\p{N} +._/#()-]{1,40}$/u

/** curated quick-pick OR any sanitized 1-40 char custom label (SR-16). */
function isAllowedMaterial(trimmed: string): boolean {
  return (MATERIALS as readonly string[]).includes(trimmed) || MATERIAL_LABEL_RE.test(trimmed)
}

function isHttpsUrl(value: string, maxLen: number): boolean {
  const v = value.trim()
  if (v === '' || v.length > maxLen) return false
  try {
    const u = new URL(v)
    return u.protocol === 'https:' && u.host !== ''
  } catch {
    return false
  }
}

/**
 * Validate a MakerProfileInput exactly the way the backend does, returning a
 * dotted-key → message map (empty when valid). Keys match validate.go's field
 * keys (`display_name`, `location.city`, `multicolor.max_colors`,
 * `colors[i].hex`, `contact.store_url`, …) so client and server errors share one
 * mapping. Pure + table-testable.
 */
export function validateMakerInput(input: MakerProfileInput): Record<string, string> {
  const fields: Record<string, string> = {}

  const displayName = (input.display_name ?? '').trim()
  if (displayName.length < 1 || displayName.length > MAKER_LIMITS.displayName) {
    fields.display_name = `Display name must be 1–${MAKER_LIMITS.displayName} characters.`
  }

  if (input.bio != null && input.bio.trim().length > MAKER_LIMITS.bio) {
    fields.bio = `Bio must be ${MAKER_LIMITS.bio} characters or fewer.`
  }

  const loc = input.location
  if (!loc || !loc.country) {
    fields['location.country'] = 'Country is required.'
  } else if (!COUNTRY_CODE_RE.test(loc.country)) {
    fields['location.country'] = 'Country must be a 2-letter uppercase ISO code.'
  }
  if (loc?.region != null && loc.region.length > MAKER_LIMITS.region) {
    fields['location.region'] = `Region must be ${MAKER_LIMITS.region} characters or fewer.`
  }
  if (loc?.city != null && loc.city.length > MAKER_LIMITS.city) {
    fields['location.city'] = `City must be ${MAKER_LIMITS.city} characters or fewer.`
  }
  if (loc?.ships_from != null && loc.ships_from !== '' && !COUNTRY_CODE_RE.test(loc.ships_from)) {
    fields['location.ships_from'] = 'Ships-from must be a 2-letter uppercase ISO code.'
  }
  const shipsTo = loc?.ships_to ?? []
  if (shipsTo.length > MAKER_LIMITS.shipsTo) {
    fields['location.ships_to'] = `You may list at most ${MAKER_LIMITS.shipsTo} ship-to countries.`
  }
  shipsTo.forEach((c, i) => {
    if (!COUNTRY_CODE_RE.test(c)) {
      fields[`location.ships_to[${i}]`] = 'Each ship-to entry must be a 2-letter uppercase ISO code.'
    }
  })

  const printers = input.printers ?? []
  if (printers.length > MAKER_LIMITS.printers) {
    fields.printers = `You may list at most ${MAKER_LIMITS.printers} printers.`
  }
  printers.forEach((p, i) => {
    const t = p.trim()
    if (t.length < 1 || t.length > MAKER_LIMITS.printerName) {
      fields[`printers[${i}]`] = `Each printer name must be 1–${MAKER_LIMITS.printerName} characters.`
    }
  })

  const materials = input.materials ?? []
  if (materials.length > MAKER_LIMITS.materials) {
    fields.materials = `You may list at most ${MAKER_LIMITS.materials} materials.`
  }
  materials.forEach((m, i) => {
    if (!isAllowedMaterial(m.trim())) {
      fields[`materials[${i}]`] = 'Unsupported material.'
    }
  })

  const mc = input.multicolor
  if (mc?.capable) {
    if (!Number.isInteger(mc.max_colors) || mc.max_colors < MAKER_LIMITS.maxColorsMin || mc.max_colors > MAKER_LIMITS.maxColorsMax) {
      fields['multicolor.max_colors'] = `max_colors must be between ${MAKER_LIMITS.maxColorsMin} and ${MAKER_LIMITS.maxColorsMax} when multicolor-capable.`
    }
  } else if (mc && mc.max_colors !== 0) {
    fields['multicolor.max_colors'] = 'max_colors must be 0 or absent when not multicolor-capable.'
  }
  const colors = mc?.colors ?? []
  if (colors.length > MAKER_LIMITS.colors) {
    fields['multicolor.colors'] = `You may list at most ${MAKER_LIMITS.colors} colors.`
  }
  colors.forEach((c, i) => {
    const name = (c.name ?? '').trim()
    if (name.length < 1 || name.length > MAKER_LIMITS.colorName) {
      fields[`multicolor.colors[${i}].name`] = `Color name must be 1–${MAKER_LIMITS.colorName} characters.`
    }
    if (!isValidHexColor(c.hex ?? '')) {
      fields[`multicolor.colors[${i}].hex`] = 'Color hex must look like #RRGGBB.'
    }
  })

  const contact = input.contact
  if (contact) {
    if (contact.store_url != null && contact.store_url.trim() !== '' && !isHttpsUrl(contact.store_url, MAKER_LIMITS.contactUrl)) {
      fields['contact.store_url'] = 'Store link must be a valid https URL.'
    }
    if (contact.discord != null && contact.discord.trim() !== '' && !isHttpsUrl(contact.discord, MAKER_LIMITS.contactUrl)) {
      fields['contact.discord'] = 'Discord must be a valid https URL.'
    }
    if (contact.instagram != null && contact.instagram.trim() !== '' && !isHttpsUrl(contact.instagram, MAKER_LIMITS.contactUrl)) {
      fields['contact.instagram'] = 'Instagram must be a valid https URL.'
    }
    if (contact.email != null && contact.email.trim() !== '') {
      const e = contact.email.trim()
      if (e.length > MAKER_LIMITS.email || !EMAIL_RE.test(e)) {
        fields['contact.email'] = 'Enter a valid email address.'
      }
    }
  }

  const tags = input.capability_tags ?? []
  if (tags.length > MAKER_LIMITS.capabilityTags) {
    fields.capability_tags = `You may list at most ${MAKER_LIMITS.capabilityTags} capability tags.`
  } else if (tags.some((t) => t.length > MAKER_LIMITS.capabilityTag)) {
    fields.capability_tags = `Each capability tag must be ${MAKER_LIMITS.capabilityTag} characters or fewer.`
  }

  if (input.turnaround != null && input.turnaround.trim().length > MAKER_LIMITS.turnaround) {
    fields.turnaround = `Turnaround must be ${MAKER_LIMITS.turnaround} characters or fewer.`
  }
  if (input.pricing_note != null && input.pricing_note.trim().length > MAKER_LIMITS.pricingNote) {
    fields.pricing_note = `Pricing note must be ${MAKER_LIMITS.pricingNote} characters or fewer.`
  }

  return fields
}

/**
 * The flat per-input error surface the editor form consumes. Each slot maps to a
 * concrete input (contact.* → store_url/email/discord/instagram; the whole
 * multicolor sub-form shares one `multicolor` slot). Nested/aggregate server
 * keys collapse onto these slots via {@link mapFieldErrors}.
 */
export interface MakerFieldErrors {
  display_name?: string
  bio?: string
  country?: string
  region?: string
  city?: string
  ships_from?: string
  ships_to?: string
  printers?: string
  materials?: string
  multicolor?: string
  store_url?: string
  email?: string
  discord?: string
  instagram?: string
  turnaround?: string
  pricing_note?: string
  capability_tags?: string
}

/** Stable order used for the top-of-form error summary (matches visual order). */
export const MAKER_FIELD_ORDER: (keyof MakerFieldErrors)[] = [
  'display_name',
  'bio',
  'country',
  'region',
  'city',
  'ships_from',
  'ships_to',
  'printers',
  'materials',
  'multicolor',
  'store_url',
  'email',
  'discord',
  'instagram',
  'turnaround',
  'pricing_note',
  'capability_tags',
]

/** base dotted key (index/sub-path stripped) → flat form slot. */
function fieldSlot(baseKey: string): keyof MakerFieldErrors | undefined {
  switch (baseKey) {
    case 'display_name':
      return 'display_name'
    case 'bio':
      return 'bio'
    case 'location':
    case 'location.country':
      return 'country'
    case 'location.region':
      return 'region'
    case 'location.city':
      return 'city'
    case 'location.ships_from':
      return 'ships_from'
    case 'location.ships_to':
      return 'ships_to'
    case 'printers':
      return 'printers'
    case 'materials':
      return 'materials'
    case 'multicolor':
    case 'multicolor.capable':
    case 'multicolor.max_colors':
    case 'multicolor.colors':
      return 'multicolor'
    case 'contact.store_url':
      return 'store_url'
    case 'contact.email':
      return 'email'
    case 'contact.discord':
      return 'discord'
    case 'contact.instagram':
      return 'instagram'
    case 'turnaround':
      return 'turnaround'
    case 'pricing_note':
      return 'pricing_note'
    case 'capability_tags':
      return 'capability_tags'
    default:
      return undefined
  }
}

/**
 * Collapse a dotted/indexed field-error map (from {@link validateMakerInput} OR a
 * server 422 `fields` payload) onto the flat {@link MakerFieldErrors} the form
 * renders. `printers[3]`, `location.ships_to[1]`, `multicolor.colors[2].hex` all
 * fold onto their parent slot; the first message per slot wins.
 */
export function mapFieldErrors(fields: Record<string, string>): MakerFieldErrors {
  const out: MakerFieldErrors = {}
  for (const [key, message] of Object.entries(fields)) {
    if (!message) continue
    const base = key.split('[')[0] // strip "[i]..." and any following sub-path
    const slot = fieldSlot(base)
    if (slot && out[slot] === undefined) {
      out[slot] = message
    }
  }
  return out
}
